/**
 * Power curve card on the Rides page (CHARTS_PLAN §6): best mean power
 * per duration on a log axis, watts or W/kg, previous period dashed.
 */
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { RideAttribution } from '../GarminAttribution.tsx';
import { usePowerCurve } from '../../hooks/useFitness.ts';
import { useStoredChoice } from '../../hooks/useStoredChoice.ts';
import {
  RANGE_DAYS,
  powerCurveRows,
  type CurveRow,
  type PowerUnit,
  type RangeDays,
} from '../../lib/chartData.ts';
import { formatDayShort, formatEffortTick } from '../../lib/chartFormat.ts';
import type { PowerCurveResponse } from '../../types/api.ts';
import {
  ChartCard,
  ChartMessage,
  ChartSkeleton,
  ChipGroup,
  Legend,
  LegendItem,
  RangeChips,
  TooltipBox,
  TooltipRow,
} from './ChartParts.tsx';

const UNITS: readonly PowerUnit[] = ['watts', 'wkg'];
const TICKS = [5, 60, 300, 1200, 3600];
const PREVIOUS_DASH = '5 4';

export function PowerCurveCard() {
  const { t } = useTranslation();
  const [range, setRange] = useStoredChoice<RangeDays>('power_curve_range', RANGE_DAYS, '90');
  return (
    <ChartCard title={t('charts.power.title')} actions={<RangeChips value={range} onChange={setRange} />}>
      <PowerCurveBody days={Number(range)} />
    </ChartCard>
  );
}

function PowerCurveBody({ days }: Readonly<{ days: number }>) {
  const { t } = useTranslation();
  const [unit, setUnit] = useStoredChoice<PowerUnit>('power_curve_unit', UNITS, 'watts');
  const { data, isLoading, isError, refetch } = usePowerCurve(days, true);
  if (isLoading) return <ChartSkeleton />;
  if (isError || !data) {
    return (
      <ChartMessage role="alert">
        {t('charts.loadError')}{' '}
        <button type="button" onClick={() => refetch()} className="underline">
          {t('dashboard.retry')}
        </button>
      </ChartMessage>
    );
  }
  if (data.best.length === 0) return <ChartMessage>{t('charts.power.empty')}</ChartMessage>;
  const shownUnit: PowerUnit = data.weight_kg ? unit : 'watts';
  return (
    <>
      {data.weight_kg ? (
        <ChipGroup
          label={t('charts.power.unit')}
          value={shownUnit}
          onChange={setUnit}
          options={[
            { value: 'watts', label: t('charts.power.watts') },
            { value: 'wkg', label: t('charts.power.wkg') },
          ]}
        />
      ) : null}
      <PowerCurveChart data={data} unit={shownUnit} days={days} />
      <RideAttribution text={data.data_sources.join(', ')} />
    </>
  );
}

function unitLabel(unit: PowerUnit, t: TFunction): string {
  return unit === 'wkg' ? t('charts.power.wkgUnit') : 'W';
}

function curveSummary(rows: CurveRow[], unit: PowerUnit, days: number, t: TFunction): string {
  const parts = rows
    .filter((r) => r.value != null && TICKS.includes(r.sec))
    .map((r) => `${formatEffortTick(r.sec)} ${r.value} ${unitLabel(unit, t)}`);
  return t('charts.power.summary', { days, parts: parts.join(', ') });
}

function PowerCurveChart({
  data,
  unit,
  days,
}: Readonly<{ data: PowerCurveResponse; unit: PowerUnit; days: number }>) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const rows = useMemo(
    () => powerCurveRows(data.best, data.previous, unit, data.weight_kg),
    [data, unit],
  );
  const hasPrevious = rows.some((r) => r.previous != null);
  const openRide = (index: number) => {
    const point = rows[index]?.point;
    if (point) navigate(`/rides/${point.ride_id}`);
  };

  return (
    <div className="flex flex-col gap-2">
      <div role="img" aria-label={curveSummary(rows, unit, days, t)} style={{ width: '100%', height: 240 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
            onClick={(state) => openRide(Number(state?.activeTooltipIndex))}
            style={{ cursor: 'pointer' }}
          >
            <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
            <XAxis
              dataKey="sec"
              type="number"
              scale="log"
              domain={[5, 3600]}
              ticks={TICKS}
              interval={0}
              allowDataOverflow
              tickFormatter={formatEffortTick}
              stroke="var(--chart-axis)"
              fontSize={11}
            />
            <YAxis stroke="var(--chart-axis)" fontSize={11} width={45} domain={['auto', 'auto']} />
            <Tooltip content={<CurveTooltip unit={unit} />} />
            {hasPrevious && (
              <Line
                type="monotone"
                dataKey="previous"
                name={t('charts.power.previous', { days })}
                stroke="var(--chart-previous)"
                strokeDasharray={PREVIOUS_DASH}
                strokeWidth={1.5}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              name={t('charts.power.current', { days })}
              stroke="var(--chart-power)"
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <Legend>
        <LegendItem color="var(--chart-power)" label={t('charts.power.current', { days })} />
        {hasPrevious && (
          <LegendItem
            color="var(--chart-previous)"
            dash={PREVIOUS_DASH}
            label={t('charts.power.previous', { days })}
          />
        )}
      </Legend>
      {/* Keyboard and screen-reader route to the rides behind each point. */}
      <ul className="sr-only">
        {rows.flatMap((r) =>
          r.point
            ? [
                <li key={r.sec}>
                  <Link to={`/rides/${r.point.ride_id}`}>
                    {t('charts.power.pointLink', {
                      duration: formatEffortTick(r.sec),
                      value: `${r.value} ${unitLabel(unit, t)}`,
                      date: formatDayShort(r.point.date, i18n.language),
                    })}
                  </Link>
                </li>,
              ]
            : [],
        )}
      </ul>
    </div>
  );
}

function CurveTooltip({
  active,
  payload,
  unit,
}: Readonly<{ active?: boolean; payload?: ReadonlyArray<{ payload?: CurveRow }>; unit: PowerUnit }>) {
  const { t, i18n } = useTranslation();
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const u = unitLabel(unit, t);
  return (
    <TooltipBox title={formatEffortTick(row.sec)}>
      {row.value != null && <TooltipRow label={t('charts.power.best')} value={`${row.value} ${u}`} />}
      {row.point && (
        <TooltipRow label={t('charts.power.ride')} value={formatDayShort(row.point.date, i18n.language)} />
      )}
      {row.point?.attribution && <TooltipRow label={t('charts.power.device')} value={row.point.attribution} />}
      {row.previous != null && (
        <TooltipRow label={t('charts.power.previousShort')} value={`${row.previous} ${u}`} />
      )}
    </TooltipBox>
  );
}
